import Take3D, { isTake3DTransitionEnabled } from "../models/Take3D";
import { ActorState } from "../models/ActorState";
import { ActorMovementType } from "../models/ActorMovementType";
import { TakeAttachment } from "../models/TakeAttachment";
import { hasLookatTarget, isNoLookat } from "../models/NoLookat";
import { getTakeEndState, isHeldByAttachment } from "./TakeEndState";

const PLAYER_ID = "player";

/**
 * One thing the compiled take does before its interactions. Ids only: the view
 * resolves names.
 */
export type TakeSetupStep =
  | { kind: "despawnActor"; actorId: string }
  | { kind: "destroyProp"; propId: string }
  /** A Prop-to-Player attachment ends: the prop leaves the hand and is destroyed. */
  | { kind: "detachProp"; propId: string; actorId: string }
  /** A Player-to-Prop attachment ends: the actor gets off the prop. */
  | { kind: "detachActor"; actorId: string; propId: string }
  | { kind: "camera"; cameraId: string; cut: boolean }
  | { kind: "inspectProp"; propId: string; tag: string; cut: boolean }
  | { kind: "spawnActor"; actorId: string; positionId: string }
  | {
      kind: "moveActor";
      actorId: string;
      positionId: string;
      movement: ActorMovementType;
      /** Player-to-Prop attachment the compiler releases right before the move. */
      releasedFromPropId?: string;
    }
  | { kind: "setOutfit"; actorId: string; outfit: string }
  | { kind: "resetCharacterParts"; actorId: string }
  | { kind: "animate"; actorId: string; stateName: string }
  | { kind: "lookAt"; actorId: string; targetId: string }
  | { kind: "stopLooking"; actorId: string }
  | { kind: "spawnProp"; propId: string; positionId: string }
  | { kind: "moveProp"; propId: string; positionId: string }
  | {
      kind: "attachProp";
      propId: string;
      actorId: string;
      tag: string;
      /** "pickedUp": the placed prop goes into the hand; "inHand": it spawns there. */
      source: "pickedUp" | "inHand";
    }
  | { kind: "attachActor"; actorId: string; propId: string; tag: string }
  | { kind: "startDelay"; seconds: number };

export interface TakeSetupPlan {
  /** Undefined when the take has no transition: the setup then runs in view. */
  transition?: { duration: number };
  /**
   * The Player's own walk. With a transition it runs before the fade, in view;
   * without one it runs alongside the setup, and the setup waits for it.
   */
  playerWalk: TakeSetupStep[];
  /** The setup, stage by stage; each stage waits for the one before it. */
  clear: TakeSetupStep[];
  spawnsAndMoves: TakeSetupStep[];
  attachments: TakeSetupStep[];
  lookAts: TakeSetupStep[];
  /** Without a transition the camera moves after the setup, in view. */
  cameraAfterSetup: TakeSetupStep[];
  /** Walks; the interactions wait until everyone has arrived. */
  walks: TakeSetupStep[];
  /** The Player's look-at and the start delay. */
  beforeInteractions: TakeSetupStep[];
}

const findActor = (take: Take3D | undefined, actorId: string) =>
  take?.actors?.find((actor) => actor.actorId === actorId);

const sameAttachment = (first?: TakeAttachment, second?: TakeAttachment) =>
  first === undefined || second === undefined
    ? first === second
    : first.id === second.id &&
      (first.tag ?? "") === (second.tag ?? "") &&
      (first.playerToProp ?? false) === (second.playerToProp ?? false);

const getCameraProperty = (take: Take3D, id: string) =>
  take.mainCamera?.properties?.find((property) => property.id === id)?.value;

const startsWithPropInspect = (take: Take3D) =>
  take.startViewMode === "propInspect" && Boolean(take.startPropInspect);

const isUsingDefaultCamera = (take: Take3D) =>
  Boolean(take.properties?.find((property) => property.id === "useDefaultCamera")?.value);

/** Mirrors the compiler's CanKeepStartingView. */
function canKeepStartingView(previous: Take3D | undefined, take: Take3D) {
  if (!previous) return false;
  if (!startsWithPropInspect(previous) && !startsWithPropInspect(take))
    return previous.mainCamera?.cameraId === take.mainCamera?.cameraId;
  if (startsWithPropInspect(previous) !== startsWithPropInspect(take)) return false;
  const from = previous.startPropInspect!;
  const to = take.startPropInspect!;
  if (
    from.propId !== to.propId ||
    from.tag !== to.tag ||
    previous.interactionPointId !== take.interactionPointId
  )
    return false;
  const positionIn = (owner: Take3D) =>
    owner.props?.find((prop) => prop.instanceId === to.propId)?.positionId;
  const oldAttachment = previous.attachments?.[to.propId];
  const newAttachment = take.attachments?.[to.propId];
  if (oldAttachment || newAttachment) {
    if (!sameAttachment(oldAttachment, newAttachment)) return false;
    const oldActor = findActor(previous, oldAttachment!.id);
    const newActor = findActor(take, newAttachment!.id);
    if (
      !oldActor ||
      !newActor ||
      oldActor.positionId !== newActor.positionId ||
      JSON.stringify(oldActor.direction) !== JSON.stringify(newActor.direction)
    )
      return false;
    return !newAttachment!.playerToProp ||
      (positionIn(previous) !== undefined && positionIn(previous) === positionIn(take));
  }
  return positionIn(previous) !== undefined && positionIn(previous) === positionIn(take);
}

function getCameraStep(
  previousTake: Take3D | undefined,
  take: Take3D,
  hasTransition: boolean,
): TakeSetupStep | undefined {
  if (canKeepStartingView(previousTake, take)) return undefined;
  const cut = hasTransition || getCameraProperty(take, "instant") === true;
  if (startsWithPropInspect(take))
    return { kind: "inspectProp", ...take.startPropInspect!, cut };
  if (!hasTransition && isUsingDefaultCamera(take)) return undefined;
  if (!take.mainCamera?.cameraId) return undefined;
  return { kind: "camera", cameraId: take.mainCamera.cameraId, cut };
}

/**
 * What the compiled take does before its interactions, in the order the
 * compiler runs it. Keep in sync with BuildScenarioWalker.PrepareTake and the
 * actor states in the compiler.
 *
 * Shown only when it changes something: the compiler also re-applies an
 * unchanged look-at, which looks the same in the game.
 */
export function getTakeSetupPlan(
  previousTake: Take3D | undefined,
  take: Take3D,
): TakeSetupPlan {
  const hasTransition = isTake3DTransitionEnabled(take);
  const plan: TakeSetupPlan = {
    transition: hasTransition ? { duration: take.transition?.duration ?? 0 } : undefined,
    playerWalk: [],
    clear: [],
    spawnsAndMoves: [],
    attachments: [],
    lookAts: [],
    cameraAfterSetup: [],
    walks: [],
    beforeInteractions: [],
  };

  // The previous take may attach or detach props during its interactions; the
  // compiler starts this take from how the previous one ends.
  const held = previousTake ? getTakeEndState(previousTake).attachments : {};
  const current = take.attachments ?? {};

  // ---- Attachments that end (computed first: moves release from them) ----
  const ending: Record<string, TakeAttachment> = {};
  const starting: Record<string, TakeAttachment> = {};
  if (previousTake) {
    const previous = previousTake.attachments ?? {};
    Object.entries(previous).forEach(([propId, attachment]) => {
      if (!sameAttachment(current[propId], attachment)) ending[propId] = attachment;
    });
    // Attached during the previous take (an Attach interaction): released when its actor moves.
    Object.entries(held).forEach(([propId, attachment]) => {
      if (current[propId] || ending[propId] || !attachment.playerToProp) return;
      const from = findActor(previousTake, attachment.id)?.positionId;
      const to = findActor(take, attachment.id)?.positionId;
      if (from && to && from !== to) ending[propId] = attachment;
    });
  }
  Object.entries(current).forEach(([propId, attachment]) => {
    if (!sameAttachment(previousTake?.attachments?.[propId], attachment) || !previousTake)
      starting[propId] = attachment;
  });

  const isInstantMove = (actorId: string) => {
    const before = findActor(previousTake, actorId);
    const after = findActor(take, actorId);
    if (!after) return false;
    const moves = !before || before.positionId !== after.positionId;
    if (!moves || after.movement !== ActorMovementType.Instant) return false;
    // A spawned NPC is placed, not moved.
    return actorId === PLAYER_ID || Boolean(before);
  };

  const releasedBeforeMove = new Map<string, string>();
  Object.entries(ending).forEach(([propId, attachment]) => {
    // The compiler only releases what it still tracks exactly that way.
    if (!sameAttachment(held[propId], attachment)) return;
    if (!findActor(take, attachment.id)) return;
    if (attachment.playerToProp && isInstantMove(attachment.id)) {
      releasedBeforeMove.set(attachment.id, propId);
      return;
    }
    plan.clear.push(
      attachment.playerToProp
        ? { kind: "detachActor", actorId: attachment.id, propId }
        : { kind: "detachProp", propId, actorId: attachment.id },
    );
  });

  // ---- Actors ----
  const actorIds = new Set<string>([
    ...(previousTake?.actors ?? []).map((actor) => actor.actorId),
    ...(take.actors ?? []).map((actor) => actor.actorId),
  ]);
  const poseSteps = (actorId: string, state: ActorState, before?: ActorState) => {
    const steps: TakeSetupStep[] = [];
    if (state.resetCharacterParts) steps.push({ kind: "resetCharacterParts", actorId });
    if (before && state.outfit && state.outfit !== before.outfit)
      steps.push({ kind: "setOutfit", actorId, outfit: state.outfit });
    if (state.animation?.stateName)
      steps.push({ kind: "animate", actorId, stateName: state.animation.stateName });
    return steps;
  };
  const lookStep = (actorId: string, state: ActorState, before?: ActorState) => {
    if (before && before.lookat === state.lookat && before.positionId === state.positionId)
      return undefined;
    if (hasLookatTarget(state.lookat))
      return { kind: "lookAt" as const, actorId, targetId: state.lookat };
    if (isNoLookat(state.lookat) && before && !isNoLookat(before.lookat))
      return { kind: "stopLooking" as const, actorId };
    return undefined;
  };

  actorIds.forEach((actorId) => {
    const before = findActor(previousTake, actorId);
    const state = findActor(take, actorId);
    const isPlayer = actorId === PLAYER_ID;

    if (!state) {
      if (before && !isPlayer) plan.clear.push({ kind: "despawnActor", actorId });
      return;
    }

    if (!before && !isPlayer) {
      plan.spawnsAndMoves.push(
        { kind: "spawnActor", actorId, positionId: state.positionId },
        ...poseSteps(actorId, state),
      );
      if (hasLookatTarget(state.lookat))
        plan.lookAts.push({ kind: "lookAt", actorId, targetId: state.lookat });
      return;
    }

    const moves = !before || before.positionId !== state.positionId;
    const look = isPlayer ? undefined : lookStep(actorId, state, before);
    const move: TakeSetupStep = {
      kind: "moveActor",
      actorId,
      positionId: state.positionId,
      movement: state.movement,
    };

    if (!moves) {
      plan.spawnsAndMoves.push(...poseSteps(actorId, state, before), ...(look ? [look] : []));
    } else if (state.movement === ActorMovementType.Instant) {
      const releasedFromPropId = releasedBeforeMove.get(actorId);
      plan.spawnsAndMoves.push(
        releasedFromPropId ? { ...move, releasedFromPropId } : move,
        ...poseSteps(actorId, state, before),
        ...(look ? [look] : []),
      );
    } else if (isPlayer && state.movement !== ActorMovementType.AutoWalk) {
      // Walk and Explore: the Player walks there themselves.
      plan.playerWalk.push(move);
    } else {
      plan.walks.push(move, ...poseSteps(actorId, state, before), ...(look ? [look] : []));
    }
  });

  // ---- Props (a prop held in a hand belongs to its attachment) ----
  const placed = (owner: Take3D | undefined) =>
    (owner?.props ?? []).filter((prop) => !isHeldByAttachment(current, prop.instanceId));
  const previousProps = previousTake ? placed(previousTake) : [];
  const currentProps = placed(take);
  previousProps
    .filter((prop) => !currentProps.some((next) => next.instanceId === prop.instanceId))
    .forEach((prop) => plan.clear.push({ kind: "destroyProp", propId: prop.instanceId }));
  currentProps.forEach((prop) => {
    const before = previousProps.find((last) => last.instanceId === prop.instanceId);
    if (!before)
      plan.spawnsAndMoves.push({ kind: "spawnProp", propId: prop.instanceId, positionId: prop.positionId });
    else if (before.positionId !== prop.positionId)
      plan.spawnsAndMoves.push({ kind: "moveProp", propId: prop.instanceId, positionId: prop.positionId });
  });

  // ---- New attachments ----
  const wasPlaced = (propId: string) =>
    (previousTake?.props ?? []).some((prop) => prop.instanceId === propId) ||
    Boolean(held[propId]?.playerToProp);
  Object.entries(starting).forEach(([propId, attachment]) => {
    // An Attach in the previous take may already have attached it like this.
    if (sameAttachment(held[propId], attachment)) return;
    plan.attachments.push(
      attachment.playerToProp
        ? { kind: "attachActor", actorId: attachment.id, propId, tag: attachment.tag }
        : {
            kind: "attachProp",
            propId,
            actorId: attachment.id,
            tag: attachment.tag,
            source: wasPlaced(propId) ? "pickedUp" : "inHand",
          },
    );
  });

  // ---- Camera ----
  const camera = getCameraStep(previousTake, take, hasTransition);
  if (camera) {
    if (hasTransition) plan.spawnsAndMoves.unshift(camera);
    else plan.cameraAfterSetup.push(camera);
  }

  // ---- Before the interactions ----
  const player = findActor(take, PLAYER_ID);
  if (player && hasLookatTarget(player.lookat))
    plan.beforeInteractions.push({ kind: "lookAt", actorId: PLAYER_ID, targetId: player.lookat });
  else if (player && isNoLookat(player.lookat))
    plan.beforeInteractions.push({ kind: "stopLooking", actorId: PLAYER_ID });
  const delay = take.interactionStartDelay ?? 0;
  if (delay > 0 && (!previousTake || previousTake.interactionPointId !== take.interactionPointId))
    plan.beforeInteractions.push({ kind: "startDelay", seconds: delay });

  return plan;
}

/**
 * Where a setup step happens, to show it on the map: the position it places
 * something on, or where the actor or prop it changes stands.
 */
export function getTakeSetupStepPositionId(
  step: TakeSetupStep,
  previousTake: Take3D | undefined,
  take: Take3D,
): string | undefined {
  const actorIn = (owner: Take3D | undefined, actorId: string) =>
    findActor(owner, actorId)?.positionId;
  const propIn = (owner: Take3D | undefined, propId: string) =>
    owner?.props?.find((prop) => prop.instanceId === propId)?.positionId;

  switch (step.kind) {
    case "spawnActor":
    case "moveActor":
    case "spawnProp":
    case "moveProp":
      return step.positionId;
    case "camera":
      return step.cameraId;
    case "despawnActor":
      return actorIn(previousTake, step.actorId);
    case "destroyProp":
      return propIn(previousTake, step.propId);
    case "inspectProp":
      return propIn(take, step.propId);
    case "attachActor":
      return propIn(take, step.propId) ?? actorIn(take, step.actorId);
    case "detachActor":
    case "detachProp":
    case "attachProp":
    case "setOutfit":
    case "resetCharacterParts":
    case "animate":
    case "lookAt":
    case "stopLooking":
      return actorIn(take, step.actorId);
    case "startDelay":
      return undefined;
  }
}
